import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { enqueueCommand } from '@/offline'
import { useToast } from '@/ui'
import { Cta, Footer, ItemRow, Strip, V1Screen, WaitCta } from '@/features/gate/v1/kit'
import { C } from '@/features/gate/v1/style'
import { shortCode, shortLot, tr } from '@/features/gate/v1/text'
import type { MarkBagsConvPayload } from '../commands'
import '../commands'
import { placePath, type BagView, type StoreState } from '../projection'
import { classifyScan, redTieList } from '../rules'
import { useRefusalText } from '../components/useRefusalText'
import { useDraftState } from '../useStore'
import { DoneScreen, Hint, IconBox, Saved, StoreHeader, Tally, WithStore } from './bits'
import { SIcon } from './icons'
import { TagScanner } from './TagScanner'

interface RedTieDraft {
  items: Array<{ container_id: string; scanned_at: string }>
}

/**
 * V1 — लाल टाई वाली बोरियाँ: tie a red tie on every bag of a lot moved to ordinary (and on an
 * orphan sack), scan each one, then लाल टाई बाँध दी sends one mark_bags_conv. Same rules as the
 * full red-tie screen (RedTiePage); the bags are then carried to the red-tie bay with Move.
 */
export function V1RedTie() {
  const navigate = useNavigate()
  const home = () => navigate('/m/store')
  return (
    <WithStore onBack={home}>{(state) => <RedTieFlow state={state} onHome={home} />}</WithStore>
  )
}

function RedTieFlow({ state, onHome }: { state: StoreState; onHome: () => void }) {
  const toast = useToast()
  const actor = useActor()
  const refusal = useRefusalText()
  const [draft, update, loaded, clear] = useDraftState<RedTieDraft>(
    `store.v1.redtie:${state.siteId}`,
    'store.v1.redtie',
    () => ({ items: [] }),
    actor.user_id,
  )
  const [saved, setSaved] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [done, setDone] = useState<{ count: number; bay: string | null } | null>(null)

  const pending = useMemo(() => redTieList(state), [state])
  // Orphan sacks (evidence NONE) made on this phone may be tied before the next download.
  const orphanIds = new Set(
    state.replacements.filter((r) => r.evidence === 'NONE').map((r) => r.payload.new_container.id),
  )
  const allowed = new Set([...pending.map((b) => b.container_id), ...orphanIds])
  const scanned = draft.items
    .map((i) => state.bags.get(i.container_id))
    .filter((b): b is BagView => !!b)
  const scannedIds = new Set(scanned.map((b) => b.container_id))
  const convBay = [...state.locations.values()].find((l) => l.role === 'CONVENTIONAL')
  const payloads = useMemo(() => [...state.bagByPayload.keys()], [state.bagByPayload])

  const byLot = new Map<string, BagView[]>()
  for (const b of pending) byLot.set(b.unit_id, [...(byLot.get(b.unit_id) ?? []), b])
  const pendingIds = new Set(pending.map((b) => b.container_id))
  const orphansScanned = scanned.filter((b) => !pendingIds.has(b.container_id)).length

  const onCode = (code: string): string | null => {
    const k = classifyScan(state, code)
    if (k.kind === 'DEAD') return refusal(k.status === 'VOID' ? 'TAG_VOID' : 'TAG_RETIRED')
    if (k.kind === 'UNKNOWN') return refusal('TAG_UNKNOWN')
    if (k.kind !== 'BAG') return refusal('TAG_WRONG_KIND')
    // Already on the list: the camera reads the same tag again, nothing to say.
    if (draft.items.some((i) => i.container_id === k.bag.container_id)) return null
    if (!allowed.has(k.bag.container_id)) return tr('store.v1.redTie.notListed')
    update((d) =>
      d.items.some((i) => i.container_id === k.bag.container_id)
        ? d
        : {
            items: [
              ...d.items,
              { container_id: k.bag.container_id, scanned_at: new Date().toISOString() },
            ],
          },
    )
    setSaved(code)
    return null
  }

  const removeLast = () => {
    setSaved(null)
    update((d) => ({ items: d.items.slice(0, -1) }))
  }

  const confirm = async () => {
    if (!scanned.length) return
    setSaving(true)
    try {
      const payload: MarkBagsConvPayload = {
        items: draft.items
          .filter((i) => state.bags.has(i.container_id))
          .map((i) => ({
            container_id: i.container_id,
            qr_payload: state.bags.get(i.container_id)!.qr_payload,
            scanned_at: i.scanned_at,
          })),
      }
      const parents = [
        ...new Set(scanned.map((b) => b.pending_command_id).filter((x): x is string => !!x)),
      ]
      await enqueueCommand({
        type: 'mark_bags_conv',
        payload,
        actorId: actor.user_id,
        parentIds: parents,
      })
      setDone({ count: payload.items.length, bay: convBay ? (convBay.name ?? convBay.code) : null })
      await clear()
    } catch (e) {
      toast.error(tr('store.v1.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  if (done)
    return (
      <DoneScreen
        title={tr('store.v1.redTie.doneTitle')}
        lines={tr('store.v1.redTie.doneLine', { count: done.count }).replaceAll('**', '')}
        chips={[
          {
            icon: 'redtie',
            text: done.bay
              ? tr('store.v1.redTie.moveNext', { bay: done.bay })
              : tr('store.v1.redTie.moveNextNoBay'),
          },
        ]}
        onHome={onHome}
      />
    )
  if (!loaded) return <V1Screen />

  const nothing = pending.length === 0 && orphanIds.size === 0
  return (
    <V1Screen
      header={<StoreHeader onBack={onHome} icon="redtie" title={tr('store.v1.redTie.title')} />}
      footer={
        <Footer>
          {scanned.length > 0 ? (
            <Cta
              label={tr('store.v1.redTie.confirm')}
              icon="ok"
              busy={saving}
              onClick={() => void confirm()}
            />
          ) : nothing ? (
            <Cta label={tr('store.v1.home')} icon="home" lead onClick={onHome} />
          ) : (
            <WaitCta>{tr('store.v1.scanOff.first')}</WaitCta>
          )}
        </Footer>
      }
    >
      {saved && <Saved text={tr('store.v1.tag', { tag: shortCode(saved) })} />}
      {nothing && scanned.length === 0 ? (
        <Strip icon="ok">{tr('store.v1.redTie.none')}</Strip>
      ) : (
        <>
          <ItemRow
            lead={<IconBox icon="redtie" size={44} iconSize={26} color={C.bad} />}
            title={tr('store.v1.redTie.need', { count: pending.length })}
            right={<Tally value={String(scanned.length)} label={tr('store.v1.redTie.tied')} />}
          />
          <Hint icon="tag" text={tr('store.v1.redTie.how')} />
          <TagScanner
            hint={tr(scanned.length ? 'store.v1.scanOn.hold' : 'store.v1.scanOff.first')}
            payloads={payloads}
            onCode={onCode}
            side={
              scanned.length > 0
                ? { icon: 'undo', label: tr('store.v1.put.removeLast'), onClick: removeLast }
                : undefined
            }
          />
        </>
      )}
      {[...byLot.entries()].map(([unit, list]) => {
        const lot = state.lots.get(unit)
        const n = list.filter((b) => scannedIds.has(b.container_id)).length
        const places = [...new Set(list.map((b) => placePath(state, b.location_id)))].filter(
          Boolean,
        )
        return (
          <ItemRow
            key={unit}
            padding="6px 10px"
            lead={
              <span style={{ color: C.ink, display: 'flex' }}>
                <SIcon name="redtie" size={28} />
              </span>
            }
            title={tr('store.v1.redTie.lot', {
              lot: lot ? shortLot(lot.code) : '—',
              n,
              m: list.length,
            })}
            line={
              places.length ? (
                <span style={{ color: C.muted }}>{places.join(' · ')}</span>
              ) : undefined
            }
            right={n === list.length ? <SIcon name="ok" size={22} sw={3} /> : undefined}
          />
        )
      })}
      {orphansScanned > 0 && (
        <ItemRow
          padding="6px 10px"
          lead={
            <span style={{ color: C.ink, display: 'flex' }}>
              <SIcon name="redtie" size={28} />
            </span>
          }
          title={tr('store.v1.redTie.newSacks', { count: orphansScanned })}
        />
      )}
    </V1Screen>
  )
}
